import {Injectable, Renderer2, RendererFactory2} from "@angular/core";
import {Timer, TimerOptions} from "./timer.service";
import {TimerStatus, TimerStrategy, TimerTiming} from "./timer.config";
import {ClientConfiguration} from "../configuration/client.config";
import {Scilearn, ContentProgression} from "../../constants";
import {EventBus} from "../../event/EventBus";
import {ModalConfig} from "../notification/modal.config";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import "rxjs/add/operator/share";
import {ActivityEvent, ACTIVITY_STATUS, MessageEventData, ActivityModalEvent} from "../../event/ActivityEvent";
import {ExitEvent, EXIT_STATUS} from "../../event/ExitEvent";
import {StudentConfiguration} from "../assignment/student.configuration";
import {filter} from "rxjs/operators";

@Injectable()
export class TimerManagerService {

    private timerStateQueue: Array<TimerTiming> = [];
    private timerList = {};
    private globalTimersStarted: boolean = false;
    private INACTIVE_TOTAL_TIME: number = 120 * 1000;
    private renderer: Renderer2;
    private globalListenFunc: Function;

    constructor(private client: ClientConfiguration,
                private studentConfiguration:  StudentConfiguration,
                private modalConfig: ModalConfig,
                private eventBus: EventBus,
                rendererFactory: RendererFactory2) {

        this.renderer = rendererFactory.createRenderer(null, null);
    }

    public initialize() {
        if (!this.client.allowTimeoutStopMode) {
            this.activateModalSubscribes();
            this.restartInactivity();
        }
    }

    private searchTypeInActionTypeList(eventType: string, eventList: Array<any>): boolean {
        return !!eventList && !!eventList.find((event) => event === eventType);
    }

    private isBeginningRaEvent(status: ACTIVITY_STATUS): boolean {
        return status === ACTIVITY_STATUS.activity_start;
    }

    private isValidAction(action: string): boolean {
        return !action.match("key") && !action.match("_");
    }

    private genStateObject(stop: boolean, pause: boolean, prohibit: boolean, prevState: any): {status:string, canInterrupt:boolean} {
        let canInterrupt:boolean = prohibit ? false : prevState.canInterrupt;
        let status:string;
        if (stop || pause) {
            status = stop ? TimerStatus.stopped : TimerStatus.paused;
        } else {
            status = prevState.status;
        }
        return {status:status, canInterrupt:canInterrupt};
    }

    private activateModalSubscribes(): void {
        this.eventBus.pipe(filter(event => event instanceof ActivityEvent)).subscribe( (event:ActivityEvent) => {
            if (this.isValidStrategyOperation(event.data, event.status)) {
                let status = event.status;
                let eventType = event.data.timing.key;
                let timing = event.data.timing;
                this.setTimerStateQueue(status, eventType, timing);
                this.setGlobalTimer(status, eventType, timing);
            }
        });

        this.eventBus.pipe(filter(event => event instanceof ActivityModalEvent)).subscribe( (event:ActivityModalEvent) => {
            if (event.status === ACTIVITY_STATUS.modal_close && event.data.type === Scilearn.TIMEOUT_MODAL) {

                if (event.data.timer.getType() === Scilearn._INACTIVE_TIMER) {
                    // if the popup had to do with inactivity
                    this.turnOffHardExitCountdown();

                    // turn back on the training timer
                    let trainingTimer = this.timerList[Scilearn._TRAINING_TIMER];
                    if (trainingTimer && trainingTimer.status === TimerStatus.started &&
                        !trainingTimer.getHardExitCountdown()) {
                        trainingTimer.continue();
                    }
                } else if (event.data.timer.getType() === Scilearn._TRAINING_TIMER) {
                    // else if the popup had to do with the training schedule being complete for the day

                    // I wanted to just remove this from the timer list but something keeps bringing it back
                    if (this.timerList[Scilearn._TRAINING_TIMER].isFinished()) {
                        this.timerList[Scilearn._TRAINING_TIMER].finish();
                    } else {
                        this.timerList[Scilearn._TRAINING_TIMER].stop();
                    }

                    // just make sure the inactive time restarts if inactive timer exists
                    if (this.timerList[Scilearn._INACTIVE_TIMER]) {
                        this.timerList[Scilearn._INACTIVE_TIMER].restart();
                    }
                }

            }
        });

    }

    private restartInactivity(): void {
        this.globalListenFunc = this.renderer.listen('document', 'mousemove', e => {
          let inactiveTimer = this.timerList[Scilearn._INACTIVE_TIMER];
            if (inactiveTimer &&
                inactiveTimer.status === TimerStatus.started &&
                !inactiveTimer.getHardExitCountdown()) {
                inactiveTimer.restart();
            }
        });
    }

    private isLastInactiveTimeout(): boolean {
        if (this.timerStateQueue.length > 0) {
            return this.isEventInactiveTimeout(this.timerStateQueue[this.timerStateQueue.length - 1].key);
        }
        return false;
    }

    private isEventInactiveTimeout(event): boolean {
        return event === "dialog_" + Scilearn._INACTIVE_TIMER;
    }

    /*
     * set timer status queue
     * @param {Boolean} figure out whether the function which triggers the operation on timer is starting or ending
     * @param {String} event name formatted with this.getTriggerEventName()
     * @param {TimerTiming} a certain timing strategy
     */
    private setTimerStateQueue(status: ACTIVITY_STATUS, eventType: string, timing: TimerTiming): TimerTiming {
        let state: TimerTiming;
        let oldState: TimerTiming;
        if (this.isBeginningRaEvent(status)) {
            /*
             * note:
             * currently the inactivity timeout dialog is of the highest priority whose priority level is 1
             * so the last element of the timer status queue should always be that of inactivity timeout dialog if any
             * once other type of event come in, the corresponding timer status should be added into the second last of
             * the status queue
             */
            if (!this.isLastInactiveTimeout()) {
                // get last status and push to the last
                oldState = this.timerStateQueue[this.timerStateQueue.length - 1];
                state = this.createTimerState(eventType, oldState, timing);
                this.timerStateQueue.push(state);
            } else {
                // get status and push to the second last
                oldState = this.timerStateQueue[this.timerStateQueue.length - 2];
                state = this.createTimerState(eventType, oldState, timing);
                this.timerStateQueue.splice(this.timerStateQueue.length - 1, 0, state);
            }
        } else {
            state = this.timerStateQueue.pop();
        }
        return state;
    }

    // do specific operation with event timer
    private setTimer(specificEventType: string, action: string): void {
        // the operation can only be carried out with the existent global timer
        Object.keys(this.timerList).forEach((eventType) => {
            let timer = this.timerList[eventType];
            if ((this.isValidAction(action)) && (!specificEventType || specificEventType === eventType)) {
                timer[action]();
            }
        });
    }

    private changeGlobalTimer(timing: TimerTiming): void {
        Object.keys(timing).forEach((action) => {
            if (this.isValidAction(action)) {
                timing[action].forEach((eventType) => {
                    this.setTimer(eventType, action);
                });
            }
        });
    }

    private recoverGlobalTimer(): void {
        let actions = {"stopped": "stop", "paused": "pause", "started": "start"};
        let goalState = this.timerStateQueue[this.timerStateQueue.length - 1];
        Object.keys(this.timerList).forEach((eventType) => {
            let timerInfo = goalState[eventType];
            let interrupt = timerInfo.canInterrupt ? "allowInterrupt" : "prohibitInterrupt";
            this.setTimer(eventType, actions[timerInfo.status]);
            this.setTimer(eventType, interrupt);
        });
    }

    private setGlobalTimer(status: ACTIVITY_STATUS, eventType: string, timing: TimerTiming): void {
      /*
       * note:
       * currently the inactivity timeout dialog is of the highest priority whose priority level is 1
       * so the last element of the timer status queue should always be that of inactivity timeout dialog if any
       * once other types of events come in when the inactivity timeout dialog exists
       * its timing operation will not be carried out at once
       */
        if (this.isBeginningRaEvent(status)) {
            if (!this.isLastInactiveTimeout() || this.isEventInactiveTimeout(eventType)) {
                this.changeGlobalTimer(timing);
            } else {
                // recover or reinstate the global timer to its former status
                this.recoverGlobalTimer();
                this.changeGlobalTimer(timing);
                this.changeGlobalTimer(this.timerStateQueue[this.timerStateQueue.length - 1]);
            }
        } else {
            // recover or reinstate the global timer to its former status
            this.recoverGlobalTimer();
        }
    }

    private createTimerState(eventType: string, oldState: any, timing: TimerTiming): TimerTiming {
        let state = new TimerTiming({ key: eventType });
        let timerTypes = Object.keys(this.timerList);
        if (oldState) {
            timerTypes.forEach(type => {
                let stop = this.searchTypeInActionTypeList(type, timing.stop);
                let pause = this.searchTypeInActionTypeList(type, timing.pause);
                let prohibit = this.searchTypeInActionTypeList(type, timing.prohibitInterrupt);
                state[type] = this.genStateObject(stop, pause, prohibit, oldState[type]);
            });
        } else {
            timerTypes.forEach(type => {
                state[type] = { status: TimerStatus.started, canInterrupt: true };
            });
        }
        return state;
    }

    private updateModalTimer(status: ACTIVITY_STATUS, eventType: string, timer: Timer): void {
        let timing = new TimerTiming({ "stop": [], "prohibitInterrupt": [Scilearn._TRAINING_TIMER] });
        let dialogEventType = "dialog_" + eventType;
        let message = "training_schedule";
        if (eventType === Scilearn._INACTIVE_TIMER) {
            timing.setStop([Scilearn._INACTIVE_TIMER]);
            message = "inactivity_timeout";
        }
        this.setTimerStateQueue(status, dialogEventType, timing);
        this.setGlobalTimer(status, dialogEventType, timing);
        if (this.isBeginningRaEvent(status)) {
            this.modalConfig.openModal(new MessageEventData({ timer: timer,
                type: Scilearn.TIMEOUT_MODAL, message: message}));
        }
    }

    private startTrainingTimer(): void {
        if (this.studentConfiguration.protocolDuration && !this.timerList[Scilearn._TRAINING_TIMER]) {
            let trainingTimer = new Timer(<TimerOptions>{
                totalTime: this.studentConfiguration.protocolDuration * 60 * 1000,
                type: Scilearn._TRAINING_TIMER
            });
            this.timerList[Scilearn._TRAINING_TIMER] = trainingTimer;
            trainingTimer.start();
            trainingTimer.timerObservable.subscribe((data) => {
                if (data === TimerStatus.timeout) {
                    this.updateModalTimer(ACTIVITY_STATUS.activity_start, Scilearn._TRAINING_TIMER, trainingTimer);
                }
            });
        }
    }

    private startInactiveTimer(): void {
        if (this.studentConfiguration.contentProgression !== ContentProgression.UNLOCKED &&
            !this.timerList[Scilearn._INACTIVE_TIMER]) {
            let inactiveTimer = new Timer(<TimerOptions>{
                totalTime: this.INACTIVE_TOTAL_TIME,
                type: Scilearn._INACTIVE_TIMER
            });
            this.timerList[Scilearn._INACTIVE_TIMER] = inactiveTimer;
            inactiveTimer.start();
            inactiveTimer.timerObservable.subscribe((data) => {
                if (data === TimerStatus.timeout) {
                    this.updateModalTimer(ACTIVITY_STATUS.activity_start, Scilearn._INACTIVE_TIMER, inactiveTimer);
                    this.timerList[Scilearn._INACTIVE_TIMER].hardExitTimer();
                } else if (data === TimerStatus.hard_exit) {
                    this.eventBus.next(new ExitEvent(EXIT_STATUS.ok));
                }
            });
        }
    }

    private isValidStrategyOperation(eventData: MessageEventData, status: ACTIVITY_STATUS): boolean {
        let isValid = true;
        if (!eventData.timing) {
            return false;
        }

        if (this.isBeginningRaEvent(status)) {
            this.timerStateQueue.forEach((state): boolean => {
                if (state.key === eventData.timing.key) {
                    isValid = false;
                    return false;
                }
                return true;
            });
        } else {
            isValid = this.timerStateQueue.filter((timing) => timing.key === eventData.timing.key).length > 0;
        }
        return isValid;
    }

    public startGlobalTimer(): void {
        if (!this.globalTimersStarted) {
            let eventType = "studentSettingSet";
            this.startInactiveTimer();
            this.startTrainingTimer();
            this.timerStateQueue.push(this.createTimerState(eventType, null, TimerStrategy[eventType]));
            this.globalTimersStarted = true;
        }
    }

    public getTimerStateQueue(): Array<TimerTiming> {
        return this.timerStateQueue;
    }

    public getTimerList(): {} {
        return this.timerList;
    }

    public stopAllTimers(): void {
        Object.keys(this.timerList).forEach((eventType) => {
            this.timerList[eventType].stop();
        });
        this.timerList = {};
        this.timerStateQueue = [];
    }

    public pauseAllTimers(): void {
        Object.keys(this.timerList).forEach((eventType) => {
            this.timerList[eventType].pause();
        });
    }

    public trainingTimeCompleted(): boolean {
        if (!!this.timerList[Scilearn._TRAINING_TIMER]) {
            return this.timerList[Scilearn._TRAINING_TIMER].getTimeLeft() === 0;
        } else {
            return true;
        }
    }

    public getTrainingTimeLeft(): string {
        return this.timerList[Scilearn._TRAINING_TIMER].showTime();
    }

    public turnOffHardExitCountdown(): void {
        this.timerList[Scilearn._INACTIVE_TIMER].setHardExitCountdown(false);
    }

}
